import { OktaAuth } from '@okta/okta-auth-js';

import routes from '@/routes/config';

const ISSUER = import.meta.env.VITE_OKTA_AUTH_ISSUER;
const CLIENT_ID = import.meta.env.VITE_OKTA_AUTH_CLIENT_ID;
const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

// Split storage strategy (RMGKB-5723):
// - Access + ID tokens → memoryStore (module-level object, cleared on page refresh).
//   Keeping these out of localStorage limits XSS exposure to the current page load only.
// - Refresh token → localStorage so sessions survive page refreshes and tab closures.
//
// Trade-off: access/ID tokens must be silently re-issued on every page refresh via the
// refresh token. autoRenew:true handles in-session expiry (typically every 1 hour) without
// user interaction. The `offline_access` scope is required for refresh token issuance.
//
// IMPORTANT: Because tokens are re-fetched on every page refresh, any auth initialisation
// code that runs on mount must NOT call Okta's /userinfo endpoint — that would blow through
// Okta's rate limit for orgs with many concurrent users. Read claims (sub, roles, etc.)
// directly from the access token instead. See AuthProvider.tsx → initializeAuth.
const memoryStore: Record<string, string> = {};

const oktaAuth = new OktaAuth({
  issuer: ISSUER,
  clientId: CLIENT_ID,
  redirectUri: `${BASE_URL}${routes.login.callback}`,
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true,
  responseType: ['token', 'id_token'],
  tokenManager: {
    autoRenew: true,
    storageProvider: {
      getItem: (key: string) => {
        if (key.includes('refresh')) return localStorage.getItem(key);
        return memoryStore[key] ?? null;
      },
      setItem: (key: string, val: string) => {
        if (key.includes('refresh')) localStorage.setItem(key, val);
        else memoryStore[key] = val;
      },
      removeItem: (key: string) => {
        localStorage.removeItem(key);
        delete memoryStore[key];
      }
    }
  } as any
});

export default oktaAuth;